import React, {useCallback, useMemo, useState} from 'react';
import {CatalogItemVO, LayerVO} from '../../api';
import {LayerId} from '@mc/react-mapio';
import {Icon, List, ListItem, Switch} from '@mc/ui-v4';
import {mcDropDown} from '@mc/react-ui-icons';
import './MobileCatalogLayerList.scss';

type Props = {
    activeLayers: LayerId[];
    data: CatalogItemVO[];
    layersConfig: {[key: LayerId]: LayerVO};
    onChange: (activeLayers: LayerId[]) => void;
    setActiveLayers: React.Dispatch<React.SetStateAction<LayerId[]>>;
};

/**
 * Mobilní drill-down katalog vrstev pro serverPublic.
 * Zobrazuje vždy jednu úroveň, kliknutím na node se přejde do podúrovně.
 * Leaf vrstvy mají switch pro zapnutí/vypnutí.
 */
export const MobileCatalogLayerList: React.FC<Props> = ({activeLayers, data, layersConfig, onChange, setActiveLayers}) => {
    // Zásobník navigace: každý prvek je {label, items}
    const [navigationStack, setNavigationStack] = useState<{label: string; items: CatalogItemVO[]}[]>([]);

    const currentItems = navigationStack.length > 0 ? navigationStack.at(-1).items : data;
    const currentTitle = navigationStack.length > 0 ? navigationStack.at(-1).label : null;

    const activeLayerIdSet = useMemo(() => new Set(activeLayers.map(Number)), [activeLayers]);

    /** Vrátí kandidátní ID popisných vrstev pro danou vrstvu. */
    const getLabelLayerIds = useCallback(
        (layerId: LayerId): number[] => {
            const numId = Number(layerId);
            return Object.entries(layersConfig)
                .filter(([, cfg]) => Number(cfg.sourceLayerId) === numId)
                .map(([id]) => Number(id))
                .filter(id => Number.isFinite(id));
        },
        [layersConfig],
    );

    /** Shromáždí ID všech listových vrstev z katalogu (rekurzivně). */
    const collectLayerIds = (item: CatalogItemVO): LayerId[] => {
        if (item.layerId && !item.children?.length) return [item.layerId];
        return (item.children ?? []).flatMap(collectLayerIds);
    };

    const allCatalogLayerIds = useMemo(() => [...new Set(data?.flatMap(collectLayerIds).map(Number) ?? [])], [data]);

    const managedLayerIdSet = useMemo(
        () => new Set<number>([...allCatalogLayerIds, ...allCatalogLayerIds.flatMap(getLabelLayerIds)]),
        [allCatalogLayerIds, getLabelLayerIds],
    );

    const noFilter = useMemo(
        () => allCatalogLayerIds.length > 0 && allCatalogLayerIds.every(id => activeLayerIdSet.has(id)),
        [allCatalogLayerIds, activeLayerIdSet],
    );

    const selectedLayerIds = useMemo(
        () => (noFilter ? [] : allCatalogLayerIds.filter(id => activeLayerIdSet.has(id))),
        [noFilter, allCatalogLayerIds, activeLayerIdSet],
    );

    /** Spočítá, kolik listových vrstev pod daným nodem je ve výběru filtru. */
    const countActiveLayers = useCallback(
        (item: CatalogItemVO): number => {
            const selected = new Set(selectedLayerIds);
            return collectLayerIds(item).filter(id => selected.has(Number(id))).length;
        },
        [selectedLayerIds],
    );

    const applyChange = useCallback(
        (next: LayerId[]): void => {
            setActiveLayers(next);
            onChange(next);
        },
        [setActiveLayers, onChange],
    );

    const handleToggle = useCallback(
        (item: CatalogItemVO, checked: boolean): void => {
            const ids = collectLayerIds(item).map(Number);
            let selected: number[];
            if (checked) {
                selected = noFilter ? ids : [...new Set([...selectedLayerIds, ...ids])];
            } else {
                const toRemove = new Set(ids);
                selected = selectedLayerIds.filter(id => !toRemove.has(id));
            }
            const effective = selected.length > 0 ? selected : allCatalogLayerIds;
            const effectiveLabelIds = [...new Set(effective.flatMap(getLabelLayerIds))];
            const untouched = activeLayers.map(Number).filter(id => !managedLayerIdSet.has(id));
            applyChange([...new Set([...untouched, ...effective, ...effectiveLabelIds])] as LayerId[]);
        },
        [activeLayers, noFilter, selectedLayerIds, allCatalogLayerIds, managedLayerIdSet, getLabelLayerIds, applyChange],
    );

    const handleDrillDown = (item: CatalogItemVO): void => {
        setNavigationStack(prev => [...prev, {label: item.label, items: item.children}]);
    };

    const handleBack = (): void => {
        setNavigationStack(prev => prev.slice(0, -1));
    };

    /** Zkratka pro zjištění, jestli item obsahuje poduzly (node) */
    const isNode = (item: CatalogItemVO): boolean => Boolean(item.children?.length);

    /** Zkratka pro zjištění, jestli je vrstva/node ve výběru filtru (u nodu všechny jeho vrstvy). */
    const isChecked = (item: CatalogItemVO): boolean => {
        if (noFilter) {
            return false;
        }
        const selected = new Set(selectedLayerIds);
        const ids = collectLayerIds(item);
        return ids.length > 0 && ids.every(id => selected.has(Number(id)));
    };

    /** Získá první písmeno labelu pro avatar */
    const getInitial = (label: string): string => (label ?? '').charAt(0).toUpperCase();

    /** Generuje barvu avataru na základě labelu */
    const getAvatarColor = (label: string): string => {
        const colors = ['#e88c30', '#3366cc', '#2eab6f', '#cc3366', '#6633cc', '#33cc99', '#cc9933', '#3399cc'];
        let hash = 0;
        for (let i = 0; i < (label ?? '').length; i++) {
            hash = (label ?? '').codePointAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    return (
        <div className="mobile-catalog-layer-list">
            {currentTitle && (
                <div className="mobile-catalog-header" role="button" tabIndex={0} onClick={handleBack} onKeyDown={e => e.key === 'Enter' && handleBack()}>
                    <Icon icon={mcDropDown} className="mobile-catalog-back-icon" />
                    <span className="mobile-catalog-header-title">{currentTitle}</span>
                </div>
            )}
            <List noHorizontalPadding>
                {currentItems?.map((item: CatalogItemVO) => {
                    const activeCount = countActiveLayers(item);
                    const checked = isChecked(item);

                    return (
                        <ListItem
                            key={`${item.id}-${item.layerId}`}
                            className="mobile-catalog-item"
                            onClick={isNode(item) ? () => handleDrillDown(item) : undefined}
                            extra={<Switch checked={checked} onChange={() => handleToggle(item, !checked)} />}
                        >
                            <div className="mobile-catalog-item-content">
                                <div className="mobile-catalog-avatar" style={{backgroundColor: getAvatarColor(item.label)}}>
                                    {getInitial(item.label)}
                                </div>
                                <span className="mobile-catalog-item-label">
                                    {item.label}
                                    {isNode(item) && ` (${activeCount})`}
                                </span>
                                {isNode(item) && <Icon icon={mcDropDown} className="mobile-catalog-chevron" />}
                            </div>
                        </ListItem>
                    );
                })}
            </List>
        </div>
    );
};
